import { useCallback, useEffect, useState } from "react";
import { Button, Empty, Popconfirm, Select, Space, Spin, Table, Tag, Tooltip } from "antd";
import { ReloadOutlined, StopOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import moment from "moment";
import ApiService from "../../../core/service/api-service";
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import { Pages } from "../../../AppRouter";

// Aktör tipi renkleri — AppLogViewer'daki kodlarla aynı dil.
const ACTOR_COLORS = {
    User: 'blue',
    Client: 'cyan',
};

// Pencere seçenekleri (dakika). "Çevrimiçi" = bu pencerede en az bir HTTP isteği atmış olmak.
const WINDOW_OPTIONS = [1, 5, 15, 60];

// Pencere ne olursa olsun son 5 dk'da isteği olan "aktif", eskisi "boşta" sayılır: kullanıcı
// 60 dk'lık pencereye baktığında da kimin gerçekten başında olduğunu ayırt edebilsin.
const ACTIVE_THRESHOLD_MINUTES = 5;

const REFRESH_MS = 15000;

export default function OnlineUsersPanel() {
    const { t } = useTranslation();
    const history = useHistory();
    const { hasPermission, user } = useUser();

    const [minutes, setMinutes] = useState(5);
    const [actors, setActors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busyUserId, setBusyUserId] = useState(null);

    const canManageUsers = hasPermission(PERMISSIONS.USER_MANAGER);

    const load = useCallback(async (showSpinner = true) => {
        if (showSpinner) setLoading(true);
        const response = await ApiService.Log.GetOnlineActors(minutes);
        if (showSpinner) setLoading(false);

        if (response.success)
            setActors(response.data ?? []);
    }, [minutes]);

    useEffect(() => {
        load();
    }, [load]);

    // Otomatik tazeleme: liste canlı olmalı, ama spinner göstermeden (ekran zıplamasın).
    useEffect(() => {
        const timer = setInterval(() => load(false), REFRESH_MS);
        return () => clearInterval(timer);
    }, [load]);

    const setEnabled = async (row, enabled) => {
        setBusyUserId(row.userId);
        const response = await ApiService.Log.SetUserEnabled(row.userId, enabled);
        setBusyUserId(null);

        if (response.success)
            load(false);
    };

    // Detay: bu kullanıcının RIS iş logları (logs koleksiyonu) — log grid'i userId ile önden filtreli açılır.
    const openUserLogs = (row) => {
        history.push(`${Pages.Log.DataGrid}?userId=${encodeURIComponent(row.userId)}`);
    };

    const columns = [
        {
            title: t('Actor'),
            key: 'actor',
            render: (_, row) => {
                const idle = moment().diff(moment(row.lastSeen), 'minutes') >= ACTIVE_THRESHOLD_MINUTES;
                return (
                    <Space direction="vertical" size={0}>
                        <Space size={4}>
                            <span
                                title={idle ? t('Idle') : t('Active')}
                                style={{
                                    display: 'inline-block',
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: idle ? '#d9d9d9' : '#52c41a'
                                }}
                            />
                            <strong>{row.actorName ?? row.actorId ?? '-'}</strong>
                            <Tag color={ACTOR_COLORS[row.actorType] ?? 'default'}>{row.actorType}</Tag>
                            {row.userEnabled === false && <Tag color="red">{t('Disabled')}</Tag>}
                        </Space>
                        <span style={{ color: 'var(--text-2)', fontSize: 12 }}>
                            {row.ip}
                            {row.clientId ? ` · ${row.clientId}` : ''}
                        </span>
                    </Space>
                );
            }
        },
        {
            title: t('Last Request'),
            key: 'lastRequest',
            render: (_, row) => (
                <Space direction="vertical" size={0}>
                    <span>
                        <Tag>{row.lastMethod}</Tag>
                        <span style={{ fontFamily: 'monospace' }}>{row.lastPath}</span>
                        {row.lastStatusCode != null && (
                            <Tag
                                style={{ marginLeft: 6 }}
                                color={row.lastStatusCode >= 500 ? 'red' : row.lastStatusCode >= 400 ? 'orange' : 'green'}
                            >
                                {row.lastStatusCode}
                            </Tag>
                        )}
                    </span>
                    <Tooltip title={moment(row.lastSeen).format('YYYY-MM-DD HH:mm:ss')}>
                        <span style={{ color: 'var(--text-2)', fontSize: 12 }}>{moment(row.lastSeen).fromNow()}</span>
                    </Tooltip>
                </Space>
            )
        },
        {
            title: t('Last Activity'),
            key: 'lastLog',
            render: (_, row) => {
                if (!row.lastLog)
                    return <span style={{ color: 'var(--text-2)' }}>-</span>;

                return (
                    <Space direction="vertical" size={0}>
                        <span>
                            <Tag>{row.lastLog.logName}</Tag>
                            {row.lastLog.description ?? row.lastLog.logType}
                        </span>
                        <Tooltip title={moment(row.lastLog.createdDate).format('YYYY-MM-DD HH:mm:ss')}>
                            <span style={{ color: 'var(--text-2)', fontSize: 12 }}>
                                {moment(row.lastLog.createdDate).fromNow()}
                            </span>
                        </Tooltip>
                    </Space>
                );
            }
        },
        {
            title: t('HTTP Requests'),
            key: 'requestCount',
            width: 130,
            render: (_, row) => (
                <Space direction="vertical" size={0}>
                    <span>{row.requestCount}</span>
                    {(row.ipCount > 1 || row.deviceCount > 1) && (
                        // Aynı hesabın aynı anda birden çok IP/cihazdan gelmesi paylaşılan hesap işaretidir.
                        <Tooltip title={t('Multiple IPs or devices in this window')}>
                            <span style={{ color: '#d46b08', fontSize: 12 }}>
                                {row.ipCount} IP · {row.deviceCount} {t('devices')}
                            </span>
                        </Tooltip>
                    )}
                </Space>
            )
        },
        {
            title: '',
            key: 'actions',
            width: 190,
            render: (_, row) => {
                if (!row.userId)
                    return null;

                // Guid'ler string olarak geliyor; büyük/küçük harf farkı yüzünden kendini pasifleştirme
                // butonu yanlışlıkla görünmesin (sunucu da ayrıca reddediyor).
                const isSelf = row.userId?.toLowerCase() === user?.data?.id?.toLowerCase();

                return (
                    <Space>
                        <Button size="small" onClick={() => openUserLogs(row)}>
                            {t('Details')}
                        </Button>
                        {canManageUsers && !isSelf && row.userEnabled !== false && (
                            <Popconfirm
                                title={t('Disable this user?')}
                                description={t('The user is blocked on their next request')}
                                okText={t('Disable')}
                                cancelText={t('Cancel')}
                                okButtonProps={{ danger: true }}
                                onConfirm={() => setEnabled(row, false)}
                            >
                                <Button size="small" danger icon={<StopOutlined />} loading={busyUserId === row.userId}>
                                    {t('Disable')}
                                </Button>
                            </Popconfirm>
                        )}
                        {canManageUsers && row.userEnabled === false && (
                            <Button size="small" loading={busyUserId === row.userId} onClick={() => setEnabled(row, true)}>
                                {t('Enable')}
                            </Button>
                        )}
                    </Space>
                );
            }
        }
    ];

    return (
        <>
            <Space wrap style={{ marginBottom: 8 }}>
                <Select
                    style={{ width: 170 }}
                    value={minutes}
                    onChange={setMinutes}
                    options={WINDOW_OPTIONS.map(m => ({ value: m, label: `${t('Last')} ${m} ${t('minutes')}` }))}
                />
                <Button icon={<ReloadOutlined />} onClick={() => load()} loading={loading}>
                    {t('Refresh')}
                </Button>
                <span style={{ color: 'var(--text-2)' }}>
                    {actors.length} {t('online')}
                </span>
            </Space>

            <Spin spinning={loading}>
                <Table
                    rowKey={(row) => `${row.actorType}:${row.actorId}`}
                    size="small"
                    columns={columns}
                    dataSource={actors}
                    pagination={false}
                    locale={{ emptyText: <Empty description={t('No one is online')} /> }}
                />
            </Spin>
        </>
    );
}
